import { Form } from 'react-bootstrap';
import PropTypes from 'prop-types';

// Curated, professional timezone list. US/Canada first (the common case for a
// US veteran-claims app), then one canonical IANA zone per remaining global
// offset so every user — current or future — has a correct, selectable home.
// Values are canonical IANA identifiers (never bare offsets/abbreviations) so
// moment-timezone date math stays correct, including DST. The GMT offset in
// each label is standard time and is informational only.
export const TIMEZONES = [
  // US & Canada
  { value: 'Pacific/Honolulu', label: '(GMT-10:00) Hawaii' },
  { value: 'America/Anchorage', label: '(GMT-09:00) Alaska' },
  { value: 'America/Los_Angeles', label: '(GMT-08:00) Pacific Time (US & Canada)' },
  { value: 'America/Phoenix', label: '(GMT-07:00) Arizona' },
  { value: 'America/Denver', label: '(GMT-07:00) Mountain Time (US & Canada)' },
  { value: 'America/Chicago', label: '(GMT-06:00) Central Time (US & Canada)' },
  { value: 'America/New_York', label: '(GMT-05:00) Eastern Time (US & Canada)' },
  { value: 'America/Halifax', label: '(GMT-04:00) Atlantic Time (Canada)' },
  // Other Americas (no-DST offsets distinct from the US zones above)
  { value: 'America/Mexico_City', label: '(GMT-06:00) Guadalajara, Mexico City' },
  { value: 'America/Bogota', label: '(GMT-05:00) Bogota, Lima, Quito' },
  { value: 'America/Santo_Domingo', label: '(GMT-04:00) Caracas, Santo Domingo' },
  { value: 'America/St_Johns', label: '(GMT-03:30) Newfoundland' },
  { value: 'America/Sao_Paulo', label: '(GMT-03:00) Brasilia, Buenos Aires' },
  // Rest of world — one canonical zone per offset
  { value: 'Etc/GMT+12', label: '(GMT-12:00) International Date Line West' },
  { value: 'Pacific/Midway', label: '(GMT-11:00) Midway Island, Samoa' },
  { value: 'Atlantic/Azores', label: '(GMT-01:00) Azores' },
  { value: 'UTC', label: '(GMT+00:00) UTC' },
  { value: 'Europe/London', label: '(GMT+00:00) London, Dublin, Lisbon' },
  { value: 'Europe/Paris', label: '(GMT+01:00) Central European Time' },
  { value: 'Europe/Bucharest', label: '(GMT+02:00) Eastern European Time' },
  { value: 'Europe/Moscow', label: '(GMT+03:00) Moscow, Baghdad' },
  { value: 'Asia/Dubai', label: '(GMT+04:00) Dubai, Abu Dhabi' },
  { value: 'Asia/Karachi', label: '(GMT+05:00) Pakistan, Tashkent' },
  { value: 'Asia/Kolkata', label: '(GMT+05:30) India, Sri Lanka' },
  { value: 'Asia/Dhaka', label: '(GMT+06:00) Almaty, Dhaka' },
  { value: 'Asia/Bangkok', label: '(GMT+07:00) Bangkok, Jakarta' },
  { value: 'Asia/Singapore', label: '(GMT+08:00) Singapore, Beijing, Hong Kong' },
  { value: 'Asia/Tokyo', label: '(GMT+09:00) Tokyo, Seoul' },
  { value: 'Australia/Sydney', label: '(GMT+10:00) Sydney, Melbourne' },
  { value: 'Pacific/Auckland', label: '(GMT+12:00) Auckland, Fiji' },
];

const TimezoneSelect = ({
  name,
  value,
  onChange,
  size = 'md',
  required = true,
}) => {
  // If an existing record holds a zone not in the curated list (legacy data),
  // show it as a disabled "current" entry: visible and not silently changed,
  // but the only re-selectable options are the curated list.
  const isLegacy = !!value && !TIMEZONES.some((tz) => tz.value === value);

  return (
    <Form.Select
      name={name}
      value={value}
      onChange={onChange}
      size={size}
      required={required}
    >
      {isLegacy && (
        <option value={value} disabled>
          {value} (current)
        </option>
      )}
      {TIMEZONES.map((tz) => (
        <option key={tz.value} value={tz.value}>
          {tz.label}
        </option>
      ))}
    </Form.Select>
  );
};

TimezoneSelect.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  size: PropTypes.string,
  required: PropTypes.bool,
};

export default TimezoneSelect;
